import { useCallback } from 'react'

import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import {
  buildPaginatedEndpoint,
  convertDatesInObject,
  getNextPageParam,
} from '@/helpers/api'

import { documentsQueryKeys } from '@/hooks/documents/types'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { Document } from '@/types/pursuit/Document'

export const fetchDocuments = async (
  pursuitId: string,
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<Document>> => {
  if (accessToken === null || accessToken === undefined) {
    return { count: 0, has_more: false, data: [] }
  }

  if (!pursuitId) {
    throw new Error('pursuitId is required')
  }

  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/pursuits/${pursuitId}/documents`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return convertDatesInObject(await res.json())
}

const selectDocuments = (data: InfiniteData<PaginatedResponse<Document>>) => {
  return data.pages
    .flatMap((page) => page.data)
    .sort((a, b) => b.last_modified.getTime() - a.last_modified.getTime())
}

interface UseDocumentsOptions<T> {
  /** Hold the fetch back until a surface that needs the list is actually shown. */
  enabled?: boolean
  /**
   * Narrow the sorted list to what the caller actually wants. Runs inside the
   * query cache, so it re-runs when the documents change rather than on every
   * render, and a caller watching one document stops re-rendering when a
   * different one moves. Must be stable across renders — a `useCallback` or a
   * function defined outside the component.
   */
  select?: (documents: Document[]) => T
}

export const useDocuments = <T = Document[]>(
  pursuitId: string,
  { enabled = true, select }: UseDocumentsOptions<T> = {},
) => {
  const user = useAuthInfo()

  const selector = useCallback(
    (data: InfiniteData<PaginatedResponse<Document>>) => {
      const documents = selectDocuments(data)
      return select ? select(documents) : (documents as unknown as T)
    },
    [select],
  )

  const query = useInfiniteQuery({
    queryFn: ({ pageParam }) =>
      fetchDocuments(pursuitId, user?.accessToken, pageParam),
    queryKey: documentsQueryKeys.pursuit({ pursuitId }),
    initialPageParam: { limit: 10000 },
    staleTime: Infinity,
    enabled,
    getNextPageParam,
    select: selector,
  })
  return query
}
